import { ref } from 'vue';

/**
 * @typedef {object} ToastOptions
 * @property {string} [content] - Content of the toast
 * @property {number} [duration=3000] - Duration of the toast
 * @property {string} [id] - Id of the toast
 * @property {('default'|'error')} [type=default] - Toast type
 * @property {boolean} [isShowCloseIcon=false] - If true, show close icon
 * @property {string} [actionLabel] - Action label
 * @property {Function} [onClickAction] - Action handler
 */

// Toast stores
export const toasts = ref([]);
export const subscribers = ref([]);

/**
 * Composables to manage toast notifications
 */
export function useToast() {
  /**
   * Subscribes to toast notifications
   * @param {Function} subscriber - The subscriber function
   * @returns {Function} - The unsubscribe function
   */
  function subscribe(subscriber) {
    subscribers.value.push(subscriber);

    return () => {
      const index = subscribers.value.indexOf(subscriber);
      subscribers.value.splice(index, 1);
    };
  }

  /**
   * Publishes toast notifications
   * @param {ToastOptions} data - The data to publish
   */
  function publish(data) {
    subscribers.value.forEach(subscriber => subscriber(data));
  }

  /**
   * Sets a toast notification
   * @param {ToastOptions} options - The data
   * @returns {number} - The toast id
   */
  // eslint-disable-next-line no-shadow
  function setToast(options) {
    const id =
      options?.id || `toast-${Math.random().toString(36).substring(2, 15)}`;

    publish({
      content: options?.content || '',
      duration: options?.duration || 3000, // 3 seconds
      type: options?.type || 'default',
      ...options,
      id,
    });
    return id;
  }

  return {
    toasts,
    subscribe,
    publish,
    setToast,
  };
}

/**
 * Sets a toast notification
 * @param {string} content - The content of the toast
 * @param {ToastOptions} options - The options
 * @returns {number} - The toast id
 */
export function setToast(content, options) {
  const { setToast: setToastFn } = useToast();

  return setToastFn({
    content,
    ...options,
  });
}
